<script>
  /**
   * One reseller: its details, its certification, its people and its customers.
   *
   * Certification is edited here because it is the access lever — moving a
   * partner to suspended stops its accounts reading customer data at their very
   * next request — so the control sits next to the list of people it affects.
   */
  import { onMount } from 'svelte';
  import { link } from 'svelte-spa-router';
  import Layout from '../components/Layout.svelte';
  import { partnersApi, customersApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import {
    Handshake, ArrowLeft, Save, Loader, AlertCircle, CheckCircle, UserPlus, Trash2, X, Palette, Users
  } from 'lucide-svelte';

  /** @type {{ params?: { id?: string } }} */
  export let params = {};

  const STATUSES = ['prospect', 'certified', 'suspended', 'terminated'];

  let loading = true;
  let saving = false;
  let message = null;

  let partner = null;
  let users = [];
  let assigned = [];
  let allCustomers = [];

  let form = null;
  let branding = {};

  let newUser = { email: '', partnerRole: 'sales', password: '' };
  let assignId = '';
  let detachingUser = null;
  let unassigningCustomer = null;

  $: partnerId = params?.id;
  $: unassignedCustomers = allCustomers.filter((c) => !c.partnerId);

  onMount(() => {
    load();
  });

  async function load() {
    loading = true;
    try {
      const [detail, customerResponse] = await Promise.all([
        partnersApi.get(partnerId),
        customersApi.getAll(),
      ]);
      partner = detail.partner;
      users = detail.users || [];
      assigned = detail.customers || [];
      allCustomers = customerResponse.customers || [];
      form = {
        name: partner.name || '',
        vatNumber: partner.vatNumber || '',
        email: partner.email || '',
        status: partner.status,
        certifiedAt: toDateInput(partner.certifiedAt),
        certifiedUntil: toDateInput(partner.certifiedUntil),
        notes: partner.notes || '',
      };
      branding = { ...(partner.branding || {}) };
    } catch (err) {
      console.error('[PartnerDetail] load failed:', err);
      message = { type: 'error', text: err.message };
    } finally {
      loading = false;
    }
  }

  function toDateInput(value) {
    if (!value) return '';
    return new Date(value).toISOString().slice(0, 10);
  }

  async function save() {
    saving = true;
    message = null;
    try {
      const changes = {
        name: form.name,
        vatNumber: form.vatNumber || null,
        email: form.email || null,
        status: form.status,
        certifiedAt: form.certifiedAt || null,
        certifiedUntil: form.certifiedUntil || null,
        notes: form.notes || null,
        // Empty strings would fail the URL/colour patterns; drop them instead.
        branding: Object.fromEntries(Object.entries(branding).filter(([, v]) => v !== '' && v != null)),
      };
      const result = await partnersApi.update(partnerId, changes);
      partner = result.partner;
      message = { type: 'success', text: $_('partners.saved') };
    } catch (err) {
      console.error('[PartnerDetail] save failed:', err);
      message = { type: 'error', text: err.message };
    } finally {
      saving = false;
    }
  }

  async function addUser() {
    if (!newUser.email.trim()) return;
    saving = true;
    message = null;
    try {
      const body = { email: newUser.email.trim(), partnerRole: newUser.partnerRole };
      if (newUser.password) body.password = newUser.password;
      await partnersApi.addUser(partnerId, body);
      newUser = { email: '', partnerRole: 'sales', password: '' };
      await load();
    } catch (err) {
      console.error('[PartnerDetail] addUser failed:', err);
      message = { type: 'error', text: err.message };
    } finally {
      saving = false;
    }
  }

  async function detach(userId) {
    try {
      await partnersApi.removeUser(partnerId, userId);
      detachingUser = null;
      await load();
    } catch (err) {
      message = { type: 'error', text: err.message };
    }
  }

  async function assignCustomer() {
    if (!assignId) return;
    try {
      await partnersApi.addCustomer(partnerId, assignId);
      assignId = '';
      await load();
    } catch (err) {
      message = { type: 'error', text: err.message };
    }
  }

  async function unassign(customerId) {
    try {
      await partnersApi.removeCustomer(partnerId, customerId);
      unassigningCustomer = null;
      await load();
    } catch (err) {
      message = { type: 'error', text: err.message };
    }
  }

  function customerLabel(customer) {
    return customer.companyName || `${customer.name || ''} ${customer.surname || ''}`.trim() || customer.id;
  }
</script>

<Layout>
  <div class="space-y-6">
    <a href="/partners" use:link class="inline-flex items-center space-x-2 text-sm text-gray-500 hover:text-primary-500">
      <ArrowLeft size={16} />
      <span>{$_('partners.backToList')}</span>
    </a>

    {#if message}
      <div class="p-4 rounded-lg flex items-center space-x-2 {message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}">
        {#if message.type === 'success'}<CheckCircle size={20} />{:else}<AlertCircle size={20} />{/if}
        <span>{message.text}</span>
        <button class="ml-auto hover:opacity-70" onclick={() => (message = null)}>&times;</button>
      </div>
    {/if}

    {#if loading}
      <div class="card p-12 text-center">
        <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
      </div>
    {:else if partner && form}
      <div class="flex items-center space-x-4">
        <div class="p-3 rounded-2xl shadow-lg" style="background: linear-gradient(to bottom right, #00bfff, #0090cc);">
          <Handshake size={28} class="text-white" />
        </div>
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{partner.name}</h1>
          <p class="text-sm text-gray-500 dark:text-gray-400 font-mono">{partner.id}</p>
        </div>
      </div>

      <!-- Details + certification -->
      <div class="card p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">{$_('partners.detailsTitle')}</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="label" for="d-name">{$_('partners.name')}</label>
            <input id="d-name" type="text" class="input w-full" bind:value={form.name} />
          </div>
          <div>
            <label class="label" for="d-vat">{$_('partners.vatNumber')}</label>
            <input id="d-vat" type="text" class="input w-full" bind:value={form.vatNumber} />
          </div>
          <div>
            <label class="label" for="d-email">{$_('partners.email')}</label>
            <input id="d-email" type="email" class="input w-full" bind:value={form.email} />
          </div>
          <div>
            <label class="label" for="d-status">{$_('partners.status')}</label>
            <select id="d-status" class="input w-full" bind:value={form.status}>
              {#each STATUSES as status}
                <option value={status}>{$_(`partners.statuses.${status}`)}</option>
              {/each}
            </select>
          </div>
          <div>
            <label class="label" for="d-from">{$_('partners.certifiedAt')}</label>
            <input id="d-from" type="date" class="input w-full" bind:value={form.certifiedAt} />
          </div>
          <div>
            <label class="label" for="d-until">{$_('partners.certifiedUntil')}</label>
            <input id="d-until" type="date" class="input w-full" bind:value={form.certifiedUntil} />
          </div>
        </div>
        <div class="mt-4">
          <label class="label" for="d-notes">{$_('partners.notes')}</label>
          <textarea id="d-notes" class="input w-full" rows="2" bind:value={form.notes}></textarea>
        </div>
      </div>

      <!-- Branding -->
      <div class="card p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1 flex items-center space-x-2">
          <Palette size={20} class="text-secondary-500" />
          <span>{$_('partners.brandingTitle')}</span>
        </h3>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">{$_('partners.brandingHint')}</p>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="label" for="b-name">{$_('partners.displayName')}</label>
            <input id="b-name" type="text" class="input w-full" bind:value={branding.displayName} />
          </div>
          <div class="md:col-span-2">
            <label class="label" for="b-logo">{$_('partners.logoUrl')}</label>
            <input id="b-logo" type="url" class="input w-full" placeholder="https://…" bind:value={branding.logoUrl} />
          </div>
          <div>
            <label class="label" for="b-primary">{$_('partners.primaryColor')}</label>
            <input id="b-primary" type="text" class="input w-full" placeholder="#00bfff" bind:value={branding.primaryColor} />
          </div>
          <div>
            <label class="label" for="b-accent">{$_('partners.accentColor')}</label>
            <input id="b-accent" type="text" class="input w-full" placeholder="#ff00ff" bind:value={branding.accentColor} />
          </div>
          <div>
            <label class="label" for="b-support">{$_('partners.supportEmail')}</label>
            <input id="b-support" type="email" class="input w-full" bind:value={branding.supportEmail} />
          </div>
        </div>
        <div class="flex justify-end mt-4">
          <button class="btn btn-primary flex items-center space-x-2" onclick={save} disabled={saving}>
            {#if saving}<Loader size={18} class="animate-spin" />{:else}<Save size={18} />{/if}
            <span>{$_('common.save')}</span>
          </button>
        </div>
      </div>

      <!-- People -->
      <div class="card p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
          <Users size={20} class="text-primary-500" />
          <span>{$_('partners.usersTitle')}</span>
        </h3>

        {#if users.length === 0}
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{$_('partners.noUsers')}</p>
        {:else}
          <div class="overflow-x-auto mb-4">
            <table class="w-full">
              <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                {#each users as user (user.userId)}
                  <tr>
                    <td class="px-2 py-3 font-mono text-xs text-gray-600 dark:text-gray-300">{user.userId}</td>
                    <td class="px-2 py-3 text-sm text-gray-900 dark:text-white">
                      {user.partnerRole === 'admin' ? $_('partners.roleAdmin') : $_('partners.roleSales')}
                    </td>
                    <td class="px-2 py-3 text-right">
                      {#if detachingUser === user.userId}
                        <span class="text-xs text-gray-500 mr-2">{$_('partners.detachConfirm')}</span>
                        <button class="text-xs font-medium text-red-600 px-2" onclick={() => detach(user.userId)}>{$_('common.confirm')}</button>
                        <button class="text-gray-500 px-1" onclick={() => (detachingUser = null)}><X size={16} /></button>
                      {:else}
                        <button class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg" onclick={() => (detachingUser = user.userId)} title={$_('partners.detach')}>
                          <Trash2 size={16} />
                        </button>
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}

        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 items-end border-t border-gray-200 dark:border-gray-700 pt-4">
          <div>
            <label class="label" for="u-email">{$_('partners.userEmail')}</label>
            <input id="u-email" type="email" class="input w-full" bind:value={newUser.email} />
          </div>
          <div>
            <label class="label" for="u-role">{$_('partners.role')}</label>
            <select id="u-role" class="input w-full" bind:value={newUser.partnerRole}>
              <option value="sales">{$_('partners.roleSales')}</option>
              <option value="admin">{$_('partners.roleAdmin')}</option>
            </select>
          </div>
          <div>
            <label class="label" for="u-password">{$_('partners.initialPassword')}</label>
            <input id="u-password" type="password" class="input w-full" bind:value={newUser.password} />
            <p class="text-xs text-gray-400 mt-1">{$_('partners.passwordHint')}</p>
          </div>
          <button class="btn btn-secondary flex items-center justify-center space-x-2" onclick={addUser} disabled={saving || !newUser.email.trim()}>
            <UserPlus size={18} />
            <span>{$_('partners.addUserButton')}</span>
          </button>
        </div>
      </div>

      <!-- Customers -->
      <div class="card p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">{$_('partners.customersTitle')}</h3>

        {#if assigned.length === 0}
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{$_('partners.noCustomers')}</p>
        {:else}
          <div class="overflow-x-auto mb-4">
            <table class="w-full">
              <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                {#each assigned as customer (customer.id)}
                  <tr>
                    <td class="px-2 py-3 font-mono text-xs text-gray-600 dark:text-gray-300">{customer.id}</td>
                    <td class="px-2 py-3 text-sm text-gray-900 dark:text-white">{customerLabel(customer)}</td>
                    <td class="px-2 py-3 text-right">
                      {#if unassigningCustomer === customer.id}
                        <span class="text-xs text-gray-500 mr-2">{$_('partners.unassignConfirm')}</span>
                        <button class="text-xs font-medium text-red-600 px-2" onclick={() => unassign(customer.id)}>{$_('common.confirm')}</button>
                        <button class="text-gray-500 px-1" onclick={() => (unassigningCustomer = null)}><X size={16} /></button>
                      {:else}
                        <button class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg" onclick={() => (unassigningCustomer = customer.id)} title={$_('partners.unassign')}>
                          <Trash2 size={16} />
                        </button>
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}

        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 items-end border-t border-gray-200 dark:border-gray-700 pt-4">
          <div class="md:col-span-3">
            <label class="label" for="c-assign">{$_('partners.assignCustomer')}</label>
            <select id="c-assign" class="input w-full" bind:value={assignId}>
              <option value="">{$_('partners.selectCustomerPlaceholder')}</option>
              {#each unassignedCustomers as customer (customer.id)}
                <option value={customer.id}>{customer.id} — {customerLabel(customer)}</option>
              {/each}
            </select>
          </div>
          <button class="btn btn-secondary" onclick={assignCustomer} disabled={!assignId}>
            {$_('partners.assignButton')}
          </button>
        </div>
      </div>
    {/if}
  </div>
</Layout>
